// Tensão (110V / 220V) dos produtos.
//
// Um produto com duas tensões continua sendo UM só produto (um só card, um só id).
// No cadastro (/admin) ele guarda a tensão principal (`voltage`, com `price`/`stock` próprios)
// e, em `voltageVariants`, a outra tensão com preço e estoque próprios:
//   { voltage: "220V", price: 1299.9, stock: 8 }
// Em api.js (getProdutos) cada produto recebe a lista `tensoes` ([{ voltage, preco, estoque }],
// sempre em ordem 110V, 220V; vazia quando o produto não tem as duas tensões) e já vem com o
// preço/estoque da tensão PADRÃO aplicados (ver tensaoPadrao()).
//
// A tensão padrão segue esta ordem:
//   1. a última tensão que o cliente escolheu na loja (localStorage "tensaoPreferida");
//   2. a tensão predominante no estado (UF) do cliente (endereço do cadastro ou CEP consultado);
//   3. a tensão principal do cadastro do produto.
// Se a tensão preferida estiver sem estoque e a outra tiver, a com estoque é a usada.
import Vue from "vue";

const CHAVE_CARRINHO = "carrinhoTensoes";
const CHAVE_PREFERIDA = "tensaoPreferida";
const CHAVE_UF = "regiaoUF";

// Tensão predominante por estado (capital/região principal). Em alguns estados a tensão varia
// de cidade para cidade; por isso o cliente sempre pode trocar a tensão na página do produto
// e no carrinho. Para ajustar uma região, basta alterar a UF correspondente abaixo.
export const TENSAO_POR_UF = {
  AC: "110V", AL: "220V", AP: "110V", AM: "110V", BA: "110V", CE: "220V",
  DF: "220V", ES: "110V", GO: "220V", MA: "220V", MT: "110V", MS: "110V",
  MG: "110V", PA: "110V", PB: "220V", PR: "110V", PE: "220V", PI: "220V",
  RJ: "110V", RN: "220V", RS: "220V", RO: "110V", RR: "110V", SC: "220V",
  SP: "110V", SE: "220V", TO: "220V",
};

function ler(chave) {
  try {
    return localStorage.getItem(chave);
  } catch (e) {
    return null;
  }
}
function gravar(chave, valor) {
  try {
    if (valor === null || valor === undefined || valor === "") localStorage.removeItem(chave);
    else localStorage.setItem(chave, valor);
  } catch (e) {
    /* armazenamento indisponível: segue sem lembrar */
  }
}

// "110V" | "220V" | "Bivolt" | "" (vazio = sem tensão).
export function normalizarTensao(valor) {
  const v = String(valor || "").trim().toLowerCase();
  if (!v) return "";
  if (v.indexOf("bivolt") !== -1) return "Bivolt";
  if (/^(110|127)/.test(v)) return "110V";
  if (/^220/.test(v)) return "220V";
  return "";
}

// "110V" -> "110 V" (texto dos botões).
export function rotuloTensao(valor) {
  const t = normalizarTensao(valor);
  if (t === "110V") return "110 V";
  if (t === "220V") return "220 V";
  return t;
}

function normalizarUF(uf) {
  const s = String(uf || "").trim().toUpperCase();
  return TENSAO_POR_UF[s] ? s : "";
}

// Estado reativo: ao descobrir a UF do cliente, quem usa tensaoDaRegiao() em computed é atualizado.
const regiao = Vue.observable({ uf: normalizarUF(ler(CHAVE_UF)), preferida: normalizarTensao(ler(CHAVE_PREFERIDA)) });

export function definirUF(uf) {
  const u = normalizarUF(uf);
  if (!u) return;
  regiao.uf = u;
  gravar(CHAVE_UF, u);
}

export function ufDoCliente() {
  return regiao.uf;
}

export function tensaoDaRegiao() {
  return regiao.uf ? TENSAO_POR_UF[regiao.uf] : "";
}

export function tensaoPreferida() {
  const t = regiao.preferida;
  return t === "110V" || t === "220V" ? t : "";
}

export function definirPreferida(tensao) {
  const t = normalizarTensao(tensao);
  if (t !== "110V" && t !== "220V") return;
  regiao.preferida = t;
  gravar(CHAVE_PREFERIDA, t);
}

function numero(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return isFinite(v) ? v : 0;
  const n = parseFloat(
    String(v).replace(/[^0-9,.-]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".")
  );
  return isFinite(n) ? n : 0;
}

// Monta a lista de tensões do produto a partir do cadastro bruto.
// `precoPrincipal` = preço completo da tensão principal (hardware + software).
// `precoSoftware` = parcela de software somada ao preço de qualquer tensão.
export function montarTensoes(p, precoPrincipal, precoSoftware) {
  const principal = normalizarTensao(p && p.voltage);
  if (principal !== "110V" && principal !== "220V") return [];
  const lista = [
    { voltage: principal, preco: numero(precoPrincipal), estoque: Math.max(0, parseInt(p.stock, 10) || 0) },
  ];
  const extras = Array.isArray(p.voltageVariants) ? p.voltageVariants : [];
  extras.forEach((v) => {
    const t = normalizarTensao(v && v.voltage);
    if ((t !== "110V" && t !== "220V") || lista.some((o) => o.voltage === t)) return;
    const hw = numero(v.price);
    if (!(hw > 0)) return;
    lista.push({
      voltage: t,
      preco: Math.round((hw + numero(precoSoftware)) * 100) / 100,
      estoque: Math.max(0, parseInt(v.stock, 10) || 0),
    });
  });
  if (lista.length < 2) return [];
  return lista.sort((a, b) => (a.voltage === "110V" ? -1 : b.voltage === "110V" ? 1 : 0));
}

// Tensão padrão para o produto (veja o topo do arquivo).
export function tensaoPadrao(produto) {
  const tensoes = (produto && produto.tensoes) || [];
  if (!tensoes.length) return "";
  const candidatas = [tensaoPreferida(), tensaoDaRegiao(), normalizarTensao(produto.voltage)]
    .concat(tensoes.map((o) => o.voltage))
    .filter((t, i, arr) => t && arr.indexOf(t) === i)
    .map((t) => tensoes.find((o) => o.voltage === t))
    .filter(Boolean);
  const comEstoque = candidatas.find((o) => o.estoque > 0);
  return (comEstoque || candidatas[0]).voltage;
}

// Cópia do produto com preço/estoque da tensão escolhida. Sem a tensão (ou produto sem as duas
// tensões) devolve o próprio produto.
export function aplicarTensao(produto, tensao) {
  if (!produto) return produto;
  const o = (produto.tensoes || []).find((x) => x.voltage === normalizarTensao(tensao));
  if (!o) return produto;
  return {
    ...produto,
    voltage: o.voltage,
    tensaoSelecionada: o.voltage,
    preco: o.preco,
    price: o.preco,
    estoque: o.estoque,
    stock: o.estoque,
    disponibilidade: o.estoque > 0 ? "Pronta entrega" : "Sob consulta",
  };
}

// Tensão escolhida para cada produto no carrinho: { [idProduto]: "110V" | "220V" } (localStorage),
// no mesmo estilo de carrinhoQtd.js (o carrinho em si continua guardando só os IDs).
function lerMapa() {
  try {
    const bruto = ler(CHAVE_CARRINHO);
    const mapa = bruto ? JSON.parse(bruto) : {};
    return mapa && typeof mapa === "object" && !Array.isArray(mapa) ? mapa : {};
  } catch (e) {
    return {};
  }
}

export const carrinhoTensao = {
  chave: CHAVE_CARRINHO,
  get(id) {
    const t = normalizarTensao(lerMapa()[String(id)]);
    return t === "110V" || t === "220V" ? t : "";
  },
  set(id, tensao) {
    const t = normalizarTensao(tensao);
    const mapa = lerMapa();
    if (t === "110V" || t === "220V") mapa[String(id)] = t;
    else delete mapa[String(id)];
    gravar(CHAVE_CARRINHO, JSON.stringify(mapa));
  },
  remove(id) {
    const mapa = lerMapa();
    delete mapa[String(id)];
    gravar(CHAVE_CARRINHO, JSON.stringify(mapa));
  },
  limpar() {
    gravar(CHAVE_CARRINHO, null);
  },
};

// Grava no carrinho a tensão em que o produto está sendo adicionado (a exibida no card/página).
// Produtos sem as duas tensões não precisam de registro.
export function registrarTensaoNoCarrinho(produto, tensao) {
  if (!produto || !produto.id || !(produto.tensoes || []).length) return;
  carrinhoTensao.set(produto.id, tensao || produto.tensaoSelecionada || tensaoPadrao(produto));
}

// Para o carrinho: produto já com a tensão guardada para ele (ou a padrão, se não houver).
export function produtoNoCarrinho(produto) {
  if (!produto || !(produto.tensoes || []).length) return produto;
  const guardada = carrinhoTensao.get(produto.id);
  const t = produto.tensoes.some((o) => o.voltage === guardada) ? guardada : tensaoPadrao(produto);
  return aplicarTensao(produto, t);
}
